import { useEffect, useState } from "react";
import { DriveStore, GlobalReducer } from "../../../store/reducers";
import { observeStore } from "../../../utils/store";

/**
 * A React hook that subscribes to specific parts of the Redux store state
 * and automatically updates the component when that state changes.
 *
 * @param store - The Redux store to subscribe to
 * @param selector - A function that selects the part of state you're interested in
 * @returns The current value of the selected state
 */
export function useStoreSelector<T>(
    store: DriveStore,
    selector: (state: GlobalReducer) => T
): T {
    const [state, setState] = useState<T>(() => selector(store.getState()));

    useEffect(() => {
        // Check if state has changed since initial render
        const currentState = selector(store.getState());
        setState(currentState);

        const unsubscribe = observeStore(store, selector, (newState) => {
            setState(newState);
        });

        return () => {
            unsubscribe();
        };
    }, [store]);

    return state;
}